import { createTheme, rem } from "@mantine/core";
import { spacing } from "./mantine/spacing";

// Breakpoints configuration
const breakpoints = {
  mobileMax: "39.99em",
  mobileMaxVideo: "56em",
  mobileMin: "40em",
  md: "42.6em",
  tabletMax: "64em",
  tabletCustomSmallMax: "48.667em",
  tabletCustomMax: "61.333em",
};

export const theme = createTheme({
  breakpoints,
  flex: {
    gap: {
      tiny: rem(2),
      normal: rem(5),
      small: rem(7.5),
      medium: rem(10),
      large: rem(20),

      // em-based
      xxs: "0.3em",
      xs: "0.5em",
      sm: "0.7em",
      base: "1em",
      md: "1.2em",
      mdPlus: "1.5em",
      mdWide: "1.7em",
      lg: "2em",
      xl: "4em",
    },

    justify: {
      center: "center",
      start: "flex-start",
      end: "flex-end",
      between: "space-between",
      around: "space-around",
      evenly: "space-evenly",
    },
    align: {
      center: "center",
      start: "flex-start",
      end: "flex-end",
      stretch: "stretch",
      baseline: "baseline",
    },
  },
  margin: {
    negativeXs: "-0.3em",
    positiveXs: "0.3em",
    none: rem(0),
    xs: rem(4),
    xsEm: "0.5em",
    xsV2Em: "0.67em",
    mdSpacious: "1.5em",
    smTight: rem(7),
    sm: rem(8),
    md: rem(12),
    lg: rem(16),
    xl: rem(20),
    xxl: rem(32),
    xxlEm: "3em",
  },
  padding: {
    none: rem(0),
    xms: rem(2),
    xs: rem(4),
    sm: rem(8),
    smEm: "0.5em",
    smMd: rem(10),
    smMdEm: "0.7em",
    md: rem(12),
    lg: rem(16),
    baseEm: "1em",
    mdSpacious: "1.5em",
    lgEm: "2em",
    lgxEm: "2.5em",
    xl: rem(20),
    xlEm: "3em",
    xxl: rem(32),
    xxllEm: "4em",
    xxlEm: "5.1em",
    xxxlEm: "5.7em",
    xxxlbEm: "6.5em",
    xxxloEm: "10em",
  },

  // Font Family
  fontFamily: {
    primary: "Proxima Nova, sans-serif",
    secondary: "Arial, Helvetica, sans-serif",
    light: "Proxima Nova Light, sans-serif",
    lightItalic: "Proxima Nova Light Italic, sans-serif",
    bold: "Proxima Nova Semibold, sans-serif",
    medium: "Proxima Nova Medium, sans-serif",
  },
  // Font Size
  fontSizes: {
    xs: rem(10),
    sm: rem(14),
    md: rem(15),
    lg: rem(16),
    xl: rem(17),
    xxl: rem(20),
  },

  // Colors
  customColors: {
    black: "#000000",
    blackLight: "#707070",
    white: "#ffffff",
    red: "#FF0000",
    green: "green",
    greenLight: "#c6e870",
    greenTooltip: "#C6E970",
    animationLoader: "#e9e9e9",
    userProfileTabsBorder: "#e0e0e0",
    tableBorder: "1px solid rgba(112,112,112, 0.2)",
    accordionPlusBorder: "1px solid rgba(112,112,112, 0.2)",
    pricingFirstBorder: "1px solid rgba(112,112,112, 0.2)",
    timeStampBorder: "1px solid rgba(112,112,112, 0.5)",
    headerBorder: "1px solid rgba(0,0,0,.1)",
    headerButtonBorder: "1px solid rgba(112,112,112, 0.2)",
    headerArrowIcon: "#535856",
    footerBorder: "1px solid rgba(255,255,255,.1)",
    footerInputBorder: "1px solid rgba(255, 255, 255, 0.4)",
    footerSocialBorder: "1px solid hsla(0,0%,100%,.1)",
    diyBorder: "1px solid #D4D6CD",
    tableSortIcon: "hsla(0,0%,44%,.4)",
    borderDotted: "1px dotted #d8d8d2",
    borderReview: "1px solid #EDF0E5",
    borderButton: "1px solid #1b3028",
    borderNotification: "1px solid rgba(112, 112, 112, 0.5)",
    borderBulk: "1px solid #C6E970",
    borderCustomPlan: "1px solid #C6E870",
    triggerHelp: "#dedede",
    tabText: "#6f6f6f",
    diyText: "#6f6f6f",
    inputHashStamp: "#6f6f6f",
    reviewAuthor: "#333333",
    pricingText: "#333333",
    buttonDarkHover: "#1b3028",
    borderStampInfoFound: "1px solid #FFE400",
    stampButtonDisabled: "#B1B1B1",
    actionIconFirst: "teal",
    actionIconSecond: "gray",
    technicalDetailDivider: "1px solid #ececec",
    technicalDetailLabel: "#7a7a74",
    technicalDetailValue: "#2c2c2a",
  },

  // Backgrounds
  customBackgrounds: {
    blackPrimary: "#1B3028",
    black: "#000000",
    white: "#ffffff",
    green: "green",
    greenLight: "#c6e870",
    tooltip: "#EDF0E5",
    freePlan: "#EDF0E5",
    timeDelta: "#EDF0E5",
    infoBlock: "#EDF0E5",
    bulkBlock: "#EDF0E5",
    infoBlock2: "#1B2F28",
    pricingSecond: "#1B2F28",
    timeAgo: "#88938F",
    reportsBlock: "#88938F",
    reportsBlock2: "#EDF0E5",
    reportsBlock3: "#1B2F28",
    reportsDownload: "#EDF0E5",
    reportsDownloadNo: "#1B2F28",
    table: "#EDF0E5",
    page: "#ededed",
    userProfileTabContent: "#ededed",
    collectionFoundBlock: "#EDF0E5",
    button: "#1B2F28",
    tabsButtonIcon: "#1a1818",
    verifyStampZone: "#f8f9fa",
    stampHashInput: "#f8f9fa",
    verifyStampZoneHover: "#f3f4f5",
    accordionPlus: "#F7F7F7",
    diy: "rgba(237, 240, 229, 0.6)",
    duplicateStampWarning: "rgba(237, 240, 229, 0.6)",
    pricingBodySvg: "#E6E6E6",
    transformDataSecurity: "rgba(237, 237, 237, 0.4)",
    stampInfoFound: "#FFE400",
    stampDisabled: "rgba(255, 255, 255, 0.5)",
    notification: "#EDF0E5",
    stampButtonDisabled: "#D0D0D0",
    selectIcon: "rgba(112, 112, 112, 0.4)",
    bg: "rgba(0, 0, 0, 0.5)",
  },

  // Common spacing values
  spacing,

  // Media query mixins
  mediaQueries: {
    mobile: `@media (max-width: ${breakpoints.xs})`,
    tablet: `@media (min-width: ${breakpoints.sm})`,
    desktop: `@media (min-width: ${breakpoints.lg})`,
    largeDesktop: `@media (min-width: ${breakpoints.xl})`,
  },
  infoBanner: {
    info: {
      color: "blue",
      bg: "rgba(34,139,230,0.1)",
    },
    warning: {
      color: "yellow",
      bg: "rgba(250,176,5,0.1)",
    },
    error: {
      color: "red",
      bg: "rgba(255,82,82,0.1)",
    },
  },
});
